export const getScanTimestamp = (scan) => {
  if (!scan?.scanDate) return 0;
  const parsed = new Date(String(scan.scanDate).replace(" ", "T"));
  const time = parsed.getTime();
  return Number.isNaN(time) ? 0 : time;
};

export const isCompleteScan = (scan) => {
  const status = (scan?.runStatus || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .trim();
  return status === "complete" && Boolean(scan?.runId);
};

export const getComparableScans = (cspmResultOrderedMap, selectedWorkload) => {
  let scans = Object.values(cspmResultOrderedMap || {}).filter(
    (scan) => scan && scan.scanDate && isCompleteScan(scan)
  );

  if (!selectedWorkload) {
    scans = scans.filter((scan) => scan.resourceScopeType === "Full Run");
  }

  return scans.sort((a, b) => getScanTimestamp(b) - getScanTimestamp(a));
};

export const formatScore = (val) => {
  if (val == null || Number.isNaN(Number(val))) return "—";
  const num = Number(val);
  if (Number.isInteger(num)) return `${num}%`;
  const fixed = num.toFixed(2);
  if (fixed.endsWith("00")) return `${num.toFixed(0)}%`;
  if (fixed.endsWith("0")) return `${num.toFixed(1)}%`;
  return `${fixed}%`;
};

export const formatDelta = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "0";
  const num = Number(value);
  const abs = Math.abs(num);
  const formatted = Number.isInteger(abs)
    ? String(abs)
    : abs.toFixed(2).replace(/\.?0+$/, "");
  if (num > 0) return `+${formatted}`;
  if (num < 0) return `-${formatted}`;
  return "0";
};

export const deltaColor = (value, { invert = false, informational = false } = {}) => {
  if (informational) return "#1976d2";
  const num = Number(value) || 0;
  if (num === 0) return "#64748b";
  const improved = invert ? num < 0 : num > 0;
  return improved ? "#10b981" : "#ef4444";
};

export const deltaBackground = (value, { invert = false, informational = false } = {}) => {
  if (informational) return { bgcolor: "#eff6ff", border: "1px solid #bfdbfe" };
  const num = Number(value) || 0;
  if (num === 0) return { bgcolor: "#f8fafc", border: "1px solid #e2e8f0" };
  const improved = invert ? num < 0 : num > 0;
  return improved
    ? { bgcolor: "#f0fdf4", border: "1px solid #c6f6d5" }
    : { bgcolor: "#fef2f2", border: "1px solid #fecaca" };
};

export const getPair = (summaryNode, firstFallback, secondFallback) => {
  const first = summaryNode?.first ?? firstFallback ?? 0;
  const second = summaryNode?.second ?? secondFallback ?? 0;
  const delta =
    summaryNode?.delta != null ? summaryNode.delta : Number(second) - Number(first);
  return { first, second, delta };
};

export const buildSummaryTiles = (result) => {
  if (!result) return [];
  const firstSummary = result.firstRun?.summary || {};
  const secondSummary = result.secondRun?.summary || {};
  const summary = result.summary || {};

  const score = getPair(
    summary.score,
    firstSummary.score,
    secondSummary.score
  );
  const scanned = getPair(
    summary.resourceCounts?.scanned,
    firstSummary.resourceCounts?.scanned,
    secondSummary.resourceCounts?.scanned
  );
  const passedResources = getPair(
    summary.resourceCounts?.passed,
    firstSummary.resourceCounts?.passed,
    secondSummary.resourceCounts?.passed
  );
  const failedResources = getPair(
    summary.resourceCounts?.failed,
    firstSummary.resourceCounts?.failed,
    secondSummary.resourceCounts?.failed
  );
  const passedChecks = getPair(
    summary.checkStatusCounts?.PASSED,
    firstSummary.checkStatusCounts?.PASSED,
    secondSummary.checkStatusCounts?.PASSED
  );
  const failedChecks = getPair(
    summary.checkStatusCounts?.FAILED,
    firstSummary.checkStatusCounts?.FAILED,
    secondSummary.checkStatusCounts?.FAILED
  );
  const critical = getPair(
    summary.severityFailedCounts?.CRITICAL,
    firstSummary.severityFailedCounts?.CRITICAL,
    secondSummary.severityFailedCounts?.CRITICAL
  );
  const high = getPair(
    summary.severityFailedCounts?.HIGH,
    firstSummary.severityFailedCounts?.HIGH,
    secondSummary.severityFailedCounts?.HIGH
  );

  return [
    {
      key: "score",
      label: "Security Score",
      first: formatScore(score.first),
      second: formatScore(score.second),
      delta: formatDelta(score.delta),
      rawDelta: score.delta,
      invert: false,
    },
    {
      key: "scanned",
      label: "Resources Scanned",
      first: scanned.first,
      second: scanned.second,
      delta: formatDelta(scanned.delta),
      rawDelta: scanned.delta,
      informational: true,
      hints: [
        `Passed ${passedResources.first} → ${passedResources.second}`,
        `Failed ${failedResources.first} → ${failedResources.second}`,
      ],
    },
    {
      key: "passedChecks",
      label: "Checks Passed",
      first: passedChecks.first,
      second: passedChecks.second,
      delta: formatDelta(passedChecks.delta),
      rawDelta: passedChecks.delta,
      invert: false,
    },
    {
      key: "failedChecks",
      label: "Checks Failed",
      first: failedChecks.first,
      second: failedChecks.second,
      delta: formatDelta(failedChecks.delta),
      rawDelta: failedChecks.delta,
      invert: true,
    },
    {
      key: "critical",
      label: "Critical Failed",
      first: critical.first,
      second: critical.second,
      delta: formatDelta(critical.delta),
      rawDelta: critical.delta,
      invert: true,
    },
    {
      key: "high",
      label: "High Failed",
      first: high.first,
      second: high.second,
      delta: formatDelta(high.delta),
      rawDelta: high.delta,
      invert: true,
    },
  ];
};

export const getStatusColor = (status) => {
  const s = (status || "").toUpperCase();
  if (s === "FAILED") return "#d32f2f";
  if (s === "PASSED") return "#388e3c";
  if (s === "MANUAL") return "#1976d2";
  return "#64748b";
};

export const getSeverityColor = (severity) => {
  const s = (severity || "").toUpperCase();
  if (s === "CRITICAL") return "#b71c1c";
  if (s === "HIGH") return "#f44336";
  if (s === "MEDIUM") return "#fbc02d";
  if (s === "LOW") return "#4caf50";
  if (s === "INFORMATIONAL") return "#1976d2";
  return "#64748b";
};
